import React, { useState } from 'react';
import { Avatar, ago } from './reviewUi';

const STATUS_TAG = {
  resolved: ['done', 'Resolved'],
  accepted: ['done', 'Accepted'],
  rejected: ['cancel', 'Rejected'],
};

/** Where a comment points, as one short tag. */
function anchorTag(c) {
  if (c.anchor_type === 'cell') return `${c.anchor.sheet ? `${c.anchor.sheet}!` : ''}${c.anchor.cell || ''}`;
  if (c.anchor_type === 'page') return `Page ${c.anchor.page || '?'}`;
  if (c.anchor_type === 'document') return c.anchor.where ? c.anchor.where : 'Whole doc';
  return null;
}

/**
 * One comment and its replies (mockup screen 4's sidebar card).
 * Every action is the server's to allow — the flags come from the API (`can_edit`,
 * `can_resolve`, `can_decide`), so the buttons and the rules can't drift.
 */
export default function CommentThread({ comment: c, replies, focused, canComment, missing, onFocus, onAction }) {
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState('');
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(c.body);
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  if (c.deleted) {
    return (
      <div className="drv-cm drv-cm--deleted">
        <span className="drv-small">A comment by {c.author_name} was deleted.</span>
      </div>
    );
  }

  const tag = anchorTag(c);
  const [stCls, stText] = STATUS_TAG[c.status] || [];
  return (
    <div className={`drv-cm${focused ? ' drv-cm--on' : ''}${c.status !== 'open' ? ' drv-cm--closed' : ''}`}
      data-cid={c.id}>
      <button type="button" className="drv-cm-head" onClick={() => onFocus(c.id)} title="Show in the document">
        <Avatar name={c.author_name} ai={c.is_ai} />
        <strong>{c.is_ai ? 'AI pre-review' : c.author_name}</strong>
        <span className="drv-small">· {ago(c.created_at)}</span>
        <span className="drv-cm-tags">
          {c.kind === 'change' && <span className="drv-tag drv-tag--change">Change</span>}
          {c.is_ai && <span className="drv-tag drv-tag--ai">AI</span>}
          {tag && <span className="drv-tag">{tag}</span>}
          {stText && <span className={`drv-pill drv-pill--${stCls}`} style={{ fontSize: 10, padding: '1px 7px' }}>{stText}</span>}
        </span>
      </button>

      {c.anchor_type === 'text' && c.anchor.selected_text && (
        <div className={`drv-quote${c.kind === 'change' ? ' drv-quote--change' : ''}`}>
          {c.kind === 'change'
            ? <><span className="drv-del">{c.anchor.selected_text}</span> → <span className="drv-ins">{c.suggested_text}</span></>
            : <>“{c.anchor.selected_text}”</>}
        </div>
      )}
      {missing && (
        <div className="drv-small" style={{ color: '#9A3412' }}>This text is no longer in the document.</div>
      )}

      {editing ? (
        <div className="drv-cm-edit">
          <textarea className="drv-textarea" value={text} onChange={(e) => setText(e.target.value)} aria-label="Edit comment" />
          <div className="drv-cm-row">
            <button type="button" className="drv-btn" onClick={() => { setEditing(false); setText(c.body); }}>Cancel</button>
            <button type="button" className="drv-btn drv-btn--primary" disabled={busy || !text.trim()}
              onClick={() => run(async () => { await onAction('edit', c, { body: text }); setEditing(false); })}>Save</button>
          </div>
        </div>
      ) : (c.body && <div className="drv-cm-body">{c.body}</div>)}

      <div className="drv-cm-actions">
        {canComment && <button type="button" onClick={() => setReplying((r) => !r)}>Reply{replies.length ? ` · ${replies.filter((r) => !r.deleted).length}` : ''}</button>}
        {!canComment && replies.length > 0 && <span className="drv-small">{replies.length} repl{replies.length === 1 ? 'y' : 'ies'}</span>}
        {c.can_decide && c.status === 'open' && (
          <>
            <button type="button" disabled={busy} onClick={() => run(() => onAction('status', c, { status: 'accepted' }))}>Accept</button>
            <button type="button" disabled={busy} onClick={() => run(() => onAction('status', c, { status: 'rejected' }))}>Reject</button>
          </>
        )}
        {c.can_resolve && c.status === 'open' && c.kind === 'comment' && (
          <button type="button" disabled={busy} onClick={() => run(() => onAction('status', c, { status: 'resolved' }))}>Resolve</button>
        )}
        {c.can_resolve && c.status !== 'open' && (
          <button type="button" disabled={busy} onClick={() => run(() => onAction('status', c, { status: 'open' }))}>Re-open</button>
        )}
        {c.can_edit && !editing && <button type="button" onClick={() => setEditing(true)}>Edit</button>}
        {c.can_edit && (
          <button type="button" className="drv-cm-danger" disabled={busy}
            onClick={() => {
              // eslint-disable-next-line no-alert
              if (window.confirm('Delete this comment?')) run(() => onAction('delete', c));
            }}>Delete</button>
        )}
      </div>

      {replies.length > 0 && (
        <div className="drv-replies">
          {replies.map((r) => (r.deleted ? (
            <div key={r.id} className="drv-small">A reply was deleted.</div>
          ) : (
            <div key={r.id} className="drv-reply">
              <div className="drv-cm-head drv-cm-head--static">
                <Avatar name={r.author_name} />
                <strong>{r.author_name}</strong>
                <span className="drv-small">· {ago(r.created_at)}</span>
              </div>
              <div className="drv-cm-body">{r.body}</div>
              {r.can_edit && (
                <div className="drv-cm-actions">
                  <button type="button" className="drv-cm-danger"
                    onClick={() => {
                      // eslint-disable-next-line no-alert
                      if (window.confirm('Delete this reply?')) onAction('delete', r);
                    }}>Delete</button>
                </div>
              )}
            </div>
          )))}
        </div>
      )}

      {replying && (
        <div className="drv-cm-edit">
          <textarea className="drv-textarea" style={{ minHeight: 56 }} value={reply} autoFocus
            onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" aria-label="Reply" />
          <div className="drv-cm-row">
            <button type="button" className="drv-btn" onClick={() => { setReplying(false); setReply(''); }}>Cancel</button>
            <button type="button" className="drv-btn drv-btn--primary" disabled={busy || !reply.trim()}
              onClick={() => run(async () => { await onAction('reply', c, { body: reply }); setReply(''); setReplying(false); })}>
              Reply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
